// src/components/PdfViewer.tsx
import React, { useState, useEffect } from "react";
import { Button, Alert, Space, Tooltip, Progress, Typography, Spin } from "antd";
import { FullscreenOutlined, DownloadOutlined, MobileOutlined } from "@ant-design/icons";
import { ReportFile } from "../types/file/FileTypes";
import { viewReport } from "../api/file/fileServiceApi";
import type { UserUuid } from "../types/identity";

const { Text } = Typography;

interface PdfViewerProps {
	report: ReportFile;
	patientId: UserUuid;
	onDownload: () => void;
	downloading?: boolean;
	downloadProgress?: number;
	onError: (message: string) => void;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ report, patientId, onDownload, downloading, downloadProgress, onError }) => {
	const [pdfUrl, setPdfUrl] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const checkMobile = () => {
			const userAgent = navigator.userAgent.toLowerCase();
			const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
			const isSmallScreen = window.innerWidth <= 768;
			setIsMobile(isMobileDevice || isSmallScreen);
		};

		checkMobile();
		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	useEffect(() => {
		loadPdf();
		return () => cleanup();
	}, [report.id]);

	const loadPdf = async () => {
		setLoading(true);
		try {
			const url = await viewReport(patientId, report.id);
			setPdfUrl(url);
		} catch (error) {
			console.error("Error loading PDF:", error);
			onError("Failed to load PDF");
		} finally {
			setLoading(false);
		}
	};

	const cleanup = () => {
		if (pdfUrl) {
			URL.revokeObjectURL(pdfUrl);
			setPdfUrl(null);
		}
	};

	const openInNewTab = () => {
		if (pdfUrl) {
			window.open(pdfUrl, "_blank");
		}
	};

	if (loading) {
		return (
			<div className="flex flex-col items-center justify-center h-full" style={{ minHeight: 400 }}>
				<Spin size="large" />
				<Text type="secondary" className="mt-4">
					Loading PDF...
				</Text>
			</div>
		);
	}

	if (!pdfUrl) {
		return (
			<div className="flex items-center justify-center h-full" style={{ minHeight: 400 }}>
				<Text type="secondary">Unable to load PDF</Text>
			</div>
		);
	}

	if (isMobile) {
		return (
			<div className="flex flex-col items-center justify-center p-8" style={{ minHeight: 400 }}>
				<MobileOutlined style={{ fontSize: 80, color: "#1890ff", marginBottom: 24 }} />
				<h3 className="text-lg font-semibold mb-4 text-center">PDF Viewer</h3>
				<Alert message="Mobile Device Detected" description="For the best viewing experience on mobile devices, please open the PDF in full screen or download it." type="info" showIcon className="mb-4" />
				<Space direction="vertical" size="middle" className="w-full max-w-sm">
					<Tooltip title="Open PDF in a new tab for full screen viewing">
						<Button type="primary" size="large" icon={<FullscreenOutlined />} onClick={openInNewTab} block>
							Open Full Screen
						</Button>
					</Tooltip>
					<Tooltip title="Download PDF to your device">
						<Button size="large" icon={<DownloadOutlined />} onClick={onDownload} loading={downloading} block>
							Download PDF
						</Button>
					</Tooltip>
				</Space>
				{downloading && downloadProgress !== undefined && (
					<div className="w-full max-w-sm mt-4">
						<Progress percent={downloadProgress} status="active" />
					</div>
				)}
			</div>
		);
	}

	return (
		<div data-testid="files-pdf-viewer" style={{ position: "relative", width: "100%", height: "calc(100vh - 200px)", minHeight: 600 }}>
			<iframe
				src={pdfUrl}
				style={{
					width: "100%",
					height: "100%",
					border: "none",
				}}
				title={report.file_name}
			/>
			<Tooltip title="Open in new tab">
				<Button
					icon={<FullscreenOutlined />}
					onClick={openInNewTab}
					style={{
						position: "absolute",
						top: 16,
						right: 16,
						zIndex: 10,
					}}
				>
					Full Screen
				</Button>
			</Tooltip>
		</div>
	);
};

export default PdfViewer;
